import isNil from 'lodash/isNil';
import isNull from 'lodash/isNull';
import differenceInDays from 'date-fns/differenceInDays';
import endOfDay from 'date-fns/endOfDay';
import startOfDay from 'date-fns/startOfDay';
import subYears from 'date-fns/subYears';
import { TIME_BREAKDOWN_OPTIONS } from './time-breakdown/constants.tsx';
import type {
	DateTimeBreakdown,
	AvailableOptions,
	TimeBreakdown,
} from './time-breakdown/types.tsx';

const getDate = (dateString?: string): Date =>
	// @ts-expect-error - TS2769 - No overload matches this call.
	isNull(dateString) ? new Date() : new Date(dateString);

/**
 * getAvailableOptionsByDateRange
 * returns array of valid ranges for given time breakdown
 */
export const getAvailableOptionsByDateRange = (
	dateTimeBreakdown: DateTimeBreakdown,
): AvailableOptions => {
	const result: { validOptions: TimeBreakdown[]; defaultOption: TimeBreakdown } = {
		validOptions: [],
		defaultOption: TIME_BREAKDOWN_OPTIONS.monthly,
	};
	const startDate = getDate(dateTimeBreakdown.startDate);
	const endDate = getDate(dateTimeBreakdown.endDate);
	const daysInBetween = differenceInDays(endDate, startDate);
	if (daysInBetween < 3) {
		result.validOptions.push(TIME_BREAKDOWN_OPTIONS.hourly);
		result.defaultOption = TIME_BREAKDOWN_OPTIONS.hourly;
	} else if (daysInBetween < 5) {
		result.validOptions.push(TIME_BREAKDOWN_OPTIONS.hourly);
		result.validOptions.push(TIME_BREAKDOWN_OPTIONS.daily);
		result.defaultOption = TIME_BREAKDOWN_OPTIONS.hourly;
	} else if (daysInBetween < 14) {
		result.validOptions.push(TIME_BREAKDOWN_OPTIONS.daily);
		result.defaultOption = TIME_BREAKDOWN_OPTIONS.daily;
	} else if (daysInBetween < 28) {
		result.validOptions.push(TIME_BREAKDOWN_OPTIONS.daily);
		result.validOptions.push(TIME_BREAKDOWN_OPTIONS.weekly);
		result.defaultOption = TIME_BREAKDOWN_OPTIONS.daily;
	} else if (daysInBetween < 60) {
		result.validOptions.push(TIME_BREAKDOWN_OPTIONS.daily);
		result.validOptions.push(TIME_BREAKDOWN_OPTIONS.weekly);
		result.defaultOption = TIME_BREAKDOWN_OPTIONS.weekly;
	} else if (daysInBetween < 92) {
		result.validOptions.push(TIME_BREAKDOWN_OPTIONS.daily);
		result.validOptions.push(TIME_BREAKDOWN_OPTIONS.weekly);
		result.validOptions.push(TIME_BREAKDOWN_OPTIONS.monthly);
		result.defaultOption = TIME_BREAKDOWN_OPTIONS.weekly;
	} else if (daysInBetween < 180) {
		result.validOptions.push(TIME_BREAKDOWN_OPTIONS.weekly);
		result.validOptions.push(TIME_BREAKDOWN_OPTIONS.monthly);
		result.defaultOption = TIME_BREAKDOWN_OPTIONS.weekly;
	} else if (daysInBetween < 366) {
		result.validOptions.push(TIME_BREAKDOWN_OPTIONS.weekly);
		result.validOptions.push(TIME_BREAKDOWN_OPTIONS.monthly);
		result.defaultOption = TIME_BREAKDOWN_OPTIONS.monthly;
	} else {
		result.validOptions.push(TIME_BREAKDOWN_OPTIONS.monthly);
	}
	return result;
};

export const getTimeBreakdownByDateRange = (
	availableOptions: AvailableOptions,
	dateQuery: DateTimeBreakdown,
): TimeBreakdown => {
	const { validOptions, defaultOption } = availableOptions;
	return dateQuery.timeBreakdown && validOptions.includes(dateQuery.timeBreakdown)
		? dateQuery.timeBreakdown
		: defaultOption;
};

export const getAllowedDateRange = (
	dateStr?: string,
): {
	min: Date;
	max: Date;
} => {
	const date = isNil(dateStr) ? new Date() : new Date(dateStr);
	const endDate = endOfDay(date);
	const startDate = new Date(subYears(startOfDay(date), 1));

	return {
		min: startDate,
		max: endDate,
	};
};
